import type { DisplayObject, MovieClip } from '@suntex.classic/createjs';
import { GraphicsUtil, UIUtil } from '@suntex.core/animation';
import { $Color, ColorInput } from '@suntex.core/color';
import { Logger } from '@suntex.core/logging';
import type { Constructor } from '@suntex.core/types';

import { BadgeType, getSkillBadgeProperties, getSkillLevel } from './utils';

export { PlayerBadge as initPlayerBadgeGraphics } from '../createjs/PlayerBadge';

export interface PlayerBadgeExportRoot extends createjs.MovieClip {
  badge: Badge;
  skillLevel: createjs.DisplayObject & {
    levelText: createjs.MovieClip;
  };
}

interface Badge extends createjs.MovieClip {
  bg: MovieClip;
  levelIcon: MovieClip;
  playerIcon: MovieClip;
  shine: DisplayObject;
  shapeOutline: MovieClip;
}

const logger = Logger.for(['renderPlayerBadge']);

type RenderPlayerBadgeParams = {
  stickers: number;
  icon: number;
  background: number;
  flatten?: boolean;
  /** Link the badge and skill level to their player pages (default true). */
  interactive?: boolean;
};

/** Creates a PlayerBadge root from the Animate library and renders it. */
export function createPlayerBadge(
  lib: Record<string, unknown>,
  params: RenderPlayerBadgeParams,
) {
  const Root = lib.PlayerBadge as Constructor<PlayerBadgeExportRoot, []>;
  const exportRoot = new Root();
  renderPlayerBadge(exportRoot, params);
  return exportRoot;
}

export function renderPlayerBadge(
  exportRoot: PlayerBadgeExportRoot,
  params: RenderPlayerBadgeParams,
) {
  logger.trace(params);
  const badge = exportRoot.badge;
  const skillLevel = exportRoot.skillLevel;

  badge.levelIcon.stop();
  badge.playerIcon.stop();

  display(BadgeType.Player);

  if (params.interactive ?? true) {
    UIUtil.createLink(badge, '/player/badge');
    UIUtil.createLink(skillLevel, '/player/achievements');
  }

  function display(type = BadgeType.Player) {
    skillLevel.levelText.stop();
    UIUtil.hide(skillLevel);
    UIUtil.setFrame(badge.shine, type);

    if (params.icon) {
      setBackgroundColor(params.background);
      setPlayerIcon(params.icon);
    } else {
      displaySkillBadge();
    }

    if (params.stickers) {
      const level = getSkillLevel(params.stickers);
      UIUtil.setFrame(skillLevel.levelText, Math.min(level, 11));
      UIUtil.show(skillLevel);
    }
  }

  function setBackgroundColor(c: ColorInput, type = BadgeType.Player) {
    const bg = GraphicsUtil.createSprite();
    const color = $Color.from(c);

    switch (type) {
      case BadgeType.School:
        GraphicsUtil.beginFill(bg, color);
        GraphicsUtil.drawPolygon(bg, 5, 80, -Math.PI / 2, 0, 10);
        break;
      case BadgeType.Team:
        GraphicsUtil.beginFill(bg, color);
        GraphicsUtil.drawRect(bg, -70, -70, 140, 140);
        break;
      default:
        if (params.flatten) {
          GraphicsUtil.beginFill(bg, color);
          GraphicsUtil.drawCircle(bg, 80, 0, 0);
        } else {
          GraphicsUtil.drawButtonBackground(bg.graphics, 80, color, 0.3);
          GraphicsUtil.drawButtonShine(bg.graphics, 80, color);
        }
        break;
    }

    badge.bg.removeAllChildren();
    badge.bg.addChild(bg);
  }

  function displaySkillBadge(type = BadgeType.Player) {
    const props = getSkillBadgeProperties(params.stickers);

    UIUtil.setFrame(badge.shine, type);
    setBackgroundColor(props.color, type);
    UIUtil.setFrame(badge.levelIcon, props.level);
    UIUtil.setFrame(badge.shapeOutline, props.shapeFrame + type * 10);

    badge.levelIcon.visible = true;
    badge.playerIcon.visible = false;
  }

  function setPlayerIcon(i: number) {
    if (i > 100) {
      badge.levelIcon.visible = true;
      badge.playerIcon.visible = false;
      UIUtil.setFrame(badge.levelIcon, i - 100);
      //GraphicsUtil.shadow(badge.levelIcon);
    } else {
      badge.levelIcon.visible = false;
      badge.playerIcon.visible = true;

      //UIUtil.delay(UIUtil.setFrame, 50, [badge.playerIcon, i]);
      UIUtil.setFrame(badge.playerIcon, i);
      //GraphicsUtil.shadow(badge.playerIcon);
    }

    UIUtil.setFrame(badge.shapeOutline, 1);
  }
}
